import { useEffect, useState, RefObject } from "react";
import styled, { keyframes } from "styled-components";

interface ScrollMoreIndicatorProps {
  scrollRef: RefObject<HTMLDivElement>;
}

/**
 * Classic RPG-style "more below" arrow.
 * Bounces with a stepped (pixel-snappy) animation and
 * disappears once the user scrolls to the bottom.
 */

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(6px); }
`;

const IndicatorWrapper = styled.div`
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 5;
`;

const Arrow = styled.div`
  font-family: "PressStart2P", serif;
  font-size: 18px;
  color: white;
  animation: ${bounce} 1s steps(2) infinite;
  text-shadow: 0 0 6px rgba(255, 255, 255, 0.4);
`;

export default function ScrollMoreIndicator({
  scrollRef,
}: ScrollMoreIndicatorProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const check = () => {
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
      setVisible(!atBottom);
    };

    el.addEventListener("scroll", check, { passive: true });
    check(); // initial check

    // Re-check if content resizes
    const observer = new ResizeObserver(check);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", check);
      observer.disconnect();
    };
  }, [scrollRef]);

  if (!visible) return null;

  return (
    <IndicatorWrapper>
      <Arrow>▼</Arrow>
    </IndicatorWrapper>
  );
}
